import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { Confirm, Modal, Table } from '@components/common';

import { organisationProjects } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import {
	resetOrganisationProjects,
	setPage,
	setPageSize,
	setSearchValue,
	setSort,
	setStatus,
} from '@slices/organisationProjects';

import { setFilterUtil } from '@utils/filterUtil';

import { IGroupOperationWithActionDTO } from '@interfaces/common/defaultDTO';
import { TSelectOption } from '@interfaces/components/componentTypes';
import { IProjectFormValues } from '@interfaces/components/form';
import { ISortBy, ITablePagination, ITableSearch, ITableSort, TTableFilter } from '@interfaces/components/table';
import {
	ICheckProjectCodeDTO,
	ICreateProjectDTO,
	IEditProjectDTO,
	IProjectDTO,
} from '@interfaces/portal/dto/projectManagementDTO';

import { TMenuActionsTypes } from '@enums/common';

import { MODAL_WIDTH_480, MODAL_WIDTH_580, modalCloseConfirm } from '@constants/modals';
import { deleteConfirmDescription, deleteConfirmText, searchTooltipText } from './constants';

import { getColumns } from './manageProjectsTableColumns';
import ProjectForm from './ProjectForm';

import styles from './ProjectsTable.module.scss';

interface IProjectsProps {
	organisationId: string;
	projectsList: IProjectDTO[];
	countOfRecords: number;
	isLoading: boolean;
	projectsStatuses: TSelectOption[];
	projectsWorkflows: TSelectOption[];
	projectAdmins: TSelectOption[];
	projectMembers: TSelectOption[];
	createProject: (data: ICreateProjectDTO) => void;
	updateProject: (data: IEditProjectDTO) => void;
	handleDeleteProject: (id: string) => void;
	handleDeleteProjects: (data: IGroupOperationWithActionDTO) => void;
	isLoadingDelete: boolean;
	handleCheckProjectCode: (data: ICheckProjectCodeDTO) => void;
	isShowDeleteProjectsConfirm: boolean;
	openDeleteProjectsConfirm: () => void;
	closeDeleteProjectsConfirm: () => void;
	singleProjectIdForRemove: string | null;
	setSingleProjectIdForRemove: (projectId: string | null) => void;
}

const ProjectsTable = ({
	organisationId,
	projectsList,
	countOfRecords,
	isLoading,
	projectsStatuses,
	projectsWorkflows,
	projectMembers,
	projectAdmins,
	createProject,
	updateProject,
	handleDeleteProject,
	handleDeleteProjects,
	isLoadingDelete,
	handleCheckProjectCode,
	isShowDeleteProjectsConfirm,
	openDeleteProjectsConfirm,
	closeDeleteProjectsConfirm,
	singleProjectIdForRemove,
	setSingleProjectIdForRemove,
}: IProjectsProps) => {
	const dispatch = useAppDispatch();

	const [selectedProjectStatus, setSelectedProjectStatus] = useState<TSelectOption<string>>(null);

	const [isProjectFormModal, setIsProjectFormModal] = useState(false);
	const [editableProject, setEditableProject] = useState<IProjectDTO | null>(null);
	const [checkedIds, setCheckedIds] = useState<Array<string>>([]);
	const [actionId, setActionId] = useState<TMenuActionsTypes>();
	const [isDirtyFormModal, setIsDirtyFormModal] = useState(false);
	const [isEditMode, setIsEditMode] = useState(false);
	const [isCloseConfirmModal, setIsCloseConfirmModal] = useState(false);

	const openProjectModal = () => setIsProjectFormModal(true);

	const openAddProjectModal = () => {
		setIsEditMode(false);
		openProjectModal();
	};

	const toggleCloseConfirmModal = () => {
		if (isDirtyFormModal) setIsCloseConfirmModal(prevState => !prevState);
		else closeModals();
	};

	const closeModals = () => {
		setIsProjectFormModal(false);
		setIsCloseConfirmModal(false);
		setEditableProject(null);
	};

	const {
		page,
		pageSize,
		searchValue,
		status,
		sortDirection,
		sortFieldName,
	} = useAppSelector(organisationProjects);

	const columns = useMemo(() => getColumns({
		setEditableProject,
		setIsEditMode,
		openProjectModal,
		openDeleteProjectsConfirm,
		setSingleProjectIdForRemove,
	}), [openDeleteProjectsConfirm, setSingleProjectIdForRemove]);

	const onSearchHandler = useCallback((searchValue: string) => dispatch(setSearchValue({ searchValue })), [dispatch]);

	const search: Array<ITableSearch> = useMemo(() => [
		{
			initialValue: searchValue,
			name: 'searchValue',
			handler: onSearchHandler,
			tooltipText: searchTooltipText,
			placeholder: searchTooltipText,
		},
	], [searchValue, onSearchHandler]);

	const handleSort = useCallback((sortBy: Array<ISortBy>) => {
		dispatch(setSort({
			sortDirection: !!sortBy[0]?.desc,
			sortFieldName: sortBy[0]?.id,
		}));
	}, [dispatch]);

	const sort: ITableSort = useMemo(() => (
		{
			initialState: [{ id: sortFieldName, desc: sortDirection }],
			handleSort,
		}
	), [sortFieldName, sortDirection, handleSort]);

	const pageHandler = useCallback((page: number) => dispatch(setPage({ page })), [dispatch]);

	const pageSizeHandler = useCallback((pageSize: number) => dispatch(setPageSize({ pageSize })), [dispatch]);

	const pagination: ITablePagination = useMemo(() => (
		{
			countOfRecords,
			forcePage: page,
			size: pageSize,
			pageHandler,
			pageSizeHandler,
		}
	), [
		countOfRecords,
		page,
		pageSize,
		pageHandler,
		pageSizeHandler,
	]);

	const filters: Array<TTableFilter> = useMemo(() => {
		return [
			{
				name: 'status',
				placeholder: 'Status',
				option: selectedProjectStatus,
				options: projectsStatuses,
				minWidth: '200px',
				setState: setSelectedProjectStatus,
				defaultValue: status,
			},
		];
	}, [projectsStatuses, selectedProjectStatus, status]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedProjectStatus,
			currentValue: status,
			setAction: setStatus,
			fieldName: 'status',
			dispatch,
		});
	}, [dispatch, status, selectedProjectStatus]);

	const submitForm = useCallback((values: IProjectFormValues) => {
		if (!organisationId) return;

		const createSubmitData: ICreateProjectDTO = {
			admins: values.admins?.map(admin => admin?.value) || [],
			members: values.members?.map(member => member?.value) || [],
			code: values.projectCode,
			description: values.description,
			name: values.projectName,
			organizationId: organisationId,
			status: values.status?.value || '',
			workflow: values.workflow?.value || '',
		};

		const editSubmitData: IEditProjectDTO = {
			projectId: editableProject?.id as string,
			dto: {
				admins: values.admins?.map(admin => admin.value) || [],
				members: values.members?.map(member => member.value) || [],
				description: values.description,
				name: values.projectName,
				status: values.status?.value || '',
			},
		};

		if (isEditMode) {
			void updateProject(editSubmitData);
		} else {
			void createProject(createSubmitData);
		}

		closeModals();
	}, [
		updateProject,
		createProject,
		isEditMode,
		organisationId,
		editableProject?.id,
	]);

	const handleCustomDelete = useCallback((data: IGroupOperationWithActionDTO) => {
		openDeleteProjectsConfirm();
		setCheckedIds(data.ids);
		setActionId(data.actionType);
	}, [openDeleteProjectsConfirm]);

	const deleteItemsHandler = useCallback(() => {
		if (singleProjectIdForRemove) {
			return handleDeleteProject(singleProjectIdForRemove);
		}

		return handleDeleteProjects({ ids: checkedIds, actionType: actionId });
	}, [
		actionId,
		checkedIds,
		handleDeleteProject,
		handleDeleteProjects,
		singleProjectIdForRemove,
	]);

	useEffect(() => {
		return () => {
			dispatch(resetOrganisationProjects());
		};
	}, [dispatch]);

	return (
		<div className={styles.projectsTableContainer}>
			<Modal
				isOpen={isProjectFormModal}
				onClose={toggleCloseConfirmModal}
				title={isEditMode ? 'Edit project' : 'Add project'}
				width={MODAL_WIDTH_580}
			>
				<ProjectForm
					editableProject={editableProject}
					handleCheckProjectCode={handleCheckProjectCode}
					isEditMode={isEditMode}
					organisationId={organisationId}
					projectAdmins={projectAdmins}
					projectMembers={projectMembers}
					projectsStatuses={projectsStatuses}
					projectsWorkflows={projectsWorkflows}
					setIsDirtyFormModal={setIsDirtyFormModal}
					submitForm={submitForm}
					toggleCloseConfirmModal={toggleCloseConfirmModal}
				/>
			</Modal>

			<Modal
				isOpen={isCloseConfirmModal}
				onClose={toggleCloseConfirmModal}
				title='Confirm action'
				width={MODAL_WIDTH_480}
			>
				<Confirm
					onClose={toggleCloseConfirmModal}
					onSubmit={closeModals}
					{...modalCloseConfirm}
				/>
			</Modal>

			<Modal
				isOpen={isShowDeleteProjectsConfirm}
				onClose={closeDeleteProjectsConfirm}
				title='Confirm action'
				width={MODAL_WIDTH_480}
			>
				<Confirm
					isLoading={isLoadingDelete}
					onClose={closeDeleteProjectsConfirm}
					onSubmit={deleteItemsHandler}
					question={deleteConfirmText}
				>
					{deleteConfirmDescription}
				</Confirm>
			</Modal>

			<Table
				columns={columns}
				data={projectsList}
				filters={filters}
				handleCustomDelete={handleCustomDelete}
				isCreatable
				isLoading={isLoading}
				isPinColumns
				maxHeight='55vh'
				pagination={pagination}
				search={search}
				searchThreshold={0}
				sort={sort}
				stickyLeftColumnIndexes={[0, 1, 2]}
				toggleAddModal={openAddProjectModal}
			/>
		</div>
	);
};

export default memo(ProjectsTable);
